/**
 * Smart pickers for workflow node config fields.
 *
 * Driven by the ``x-tray-picker`` JSON Schema extension emitted by
 * pydantic ``json_schema_extra`` on Field definitions (see
 * ``backend/src/hub/workflow/catalog.py``). The renderer in
 * NodeEditorDrawer reads the extension and dispatches here instead of
 * showing a generic text input.
 *
 * Adding a new picker kind:
 *   1. Pick a string id (kebab or snake — convention: snake).
 *   2. Annotate the pydantic Field: ``json_schema_extra={"x-tray-picker": "<id>"}``
 *   3. Add a case below.
 *   4. (Optional) Update the registry list in PICKER_KINDS for docs.
 *
 * Unknown picker ids fall back to a text input + a small "unknown
 * picker" warning — so the form remains usable while a refresh is
 * pending.
 */
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { Input } from "@/components/ui/input";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { appsApi } from "@/lib/api-apps";
import { canonicalApi, type CanonicalSummary } from "@/lib/api-canonical";
import { etlApi } from "@/lib/api-etl";
import { mappingsApi } from "@/lib/api-mappings";
import { dbIntrospect, workflowsApi } from "@/lib/api-workflows";
import { cn } from "@/lib/cn";


export const PICKER_KINDS = [
  "node_id",
  "canonical_model",
  "canonical_version",
  "mapping",
  "db_schema",
  "db_table",
  "db_columns",
  // ADR-0013 — call_workflow's target: an existing orchestration workflow.
  "workflow",
  // ADR-0038 P3 — run_etl_pipeline's target: an existing ETL pipeline.
  "etl_pipeline",
  // ADR-0010 D10 — an app data table (row_changed trigger, data_table node).
  "app_table",
  // data_table upsert's conflict target — the sibling table's unique sets.
  "app_unique_set",
] as const;
// Note: ADR-0008 PR3 used to register a "workflow" picker here for the
// SINK-BRANCH link UX, which was later scrapped (sub-flows are built
// INLINE on the canvas — see WorkflowEditor sub-flow region rendering,
// PR3b3; BranchesEditor short-circuits the SinkBranch.linked_workflow_id
// picker). The "workflow" picker is BACK for a different consumer:
// ADR-0013's ``call_workflow`` node, which references a standalone
// orchestration workflow to run synchronously as a sub-routine.

export type PickerKind = (typeof PICKER_KINDS)[number];


export interface SmartPickerProps {
  pickerKind: string;
  value: unknown;
  onChange: (v: unknown) => void;
  /** Full node config — needed by sibling-aware pickers like
   *  ``canonical_version`` (which depends on ``canonical_model``). */
  config: Record<string, unknown>;
  /** Canvas-local nodes for the node_id picker. */
  nodesInCanvas?: { id: string; label: string | null; kind: string }[];
  /** ID of the node currently being edited — used to filter it out
   *  from node_id picker so a branch doesn't pick itself. */
  currentNodeId?: string;
  /** ADR-0010 D10 — the app this workflow is bound to (app-kind only).
   *  Required by the ``app_table`` picker to scope the table list. */
  appId?: string;
  id?: string;
  placeholder?: string;
}


export function SmartPicker(props: SmartPickerProps) {
  switch (props.pickerKind) {
    case "node_id":
      return <NodeIdPicker {...props} />;
    case "canonical_model":
      return <CanonicalModelPicker {...props} />;
    case "canonical_version":
      return <CanonicalVersionPicker {...props} />;
    case "mapping":
      return <MappingPicker {...props} />;
    case "db_schema":
      return <DbSchemaPicker {...props} />;
    case "db_table":
      return <DbTablePicker {...props} />;
    case "db_columns":
      return <DbColumnsPicker {...props} />;
    case "workflow":
      return <WorkflowPicker {...props} />;
    case "etl_pipeline":
      return <EtlPipelinePicker {...props} />;
    case "app_table":
      return <AppTablePicker {...props} />;
    case "app_unique_set":
      return <AppUniqueSetPicker {...props} />;
    default:
      return <UnknownPicker {...props} />;
  }
}


// ---------------------------------------------------------------------------
// mapping picker — Combobox over /api/admin/mappings
// ---------------------------------------------------------------------------
//
// The richer ``call_mapping`` editor (CallMappingEditor.tsx) is the
// primary surface for picking a mapping AND configuring its bindings.
// This picker is the fallback for any other config field that wants a
// reference to a Mapping by id — it returns the UUID as a string.


function MappingPicker({ value, onChange, id, placeholder }: SmartPickerProps) {
  const q = useQuery({
    queryKey: ["mappings", "for-picker"],
    queryFn: async () => {
      // page_size is constrained to {25, 50, 100} by PageParams.
      const r = await mappingsApi.list({ page_size: 100 });
      return r.items;
    },
    staleTime: 30_000,
  });

  const options = React.useMemo<ComboboxOption[]>(() => {
    return (q.data ?? [])
      .map((m) => ({
        value: m.id,
        label: m.name,
        description: m.enabled
          ? `${m.strategy_kind} • ${m.inputs.length}→${m.outputs.length}`
          : `${m.strategy_kind} • disabled`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [q.data]);

  return (
    <Combobox
      id={id}
      options={options}
      value={(value as string) || null}
      onChange={(v) => onChange(v)}
      placeholder={placeholder ?? "Pick a mapping…"}
      loading={q.isLoading}
      emptyText="No mappings defined yet."
    />
  );
}


// ---------------------------------------------------------------------------
// workflow picker — Combobox over /api/admin/workflows?kind=orchestration
// ---------------------------------------------------------------------------
//
// ADR-0013: the ``call_workflow`` node references the orchestration
// workflow to run synchronously. Only orchestration workflows are
// listable here — canonical ones emit events and aren't valid as a pure
// sub-routine (the backend validator rejects them on save too).


function WorkflowPicker({ value, onChange, id, placeholder }: SmartPickerProps) {
  const q = useQuery({
    queryKey: ["workflows", "for-call-picker"],
    queryFn: async () => {
      const r = await workflowsApi.list({ kind: "orchestration", page_size: 100 });
      return r.items;
    },
    staleTime: 30_000,
  });

  const options = React.useMemo<ComboboxOption[]>(() => {
    return (q.data ?? [])
      // A callable target must have an active version — calling a
      // draft-only workflow fails at runtime, so don't offer it.
      .filter((w) => w.active_version_id != null)
      .map((w) => ({
        value: w.id,
        label: w.name,
        description: w.enabled
          ? `v${w.active_version_number ?? "?"}`
          : "disabled",
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [q.data]);

  return (
    <Combobox
      id={id}
      options={options}
      value={(value as string) || null}
      onChange={(v) => onChange(v)}
      placeholder={placeholder ?? "Pick a workflow…"}
      loading={q.isLoading}
      emptyText="No orchestration workflows with an active version."
    />
  );
}


// ---------------------------------------------------------------------------
// etl_pipeline picker — Combobox over /api/admin/etl
// ---------------------------------------------------------------------------
//
// ADR-0038 P3: the ``run_etl_pipeline`` node references the ETL pipeline to
// fire. Only pipelines with an active version are offered — running a
// draft-only pipeline has no version to execute (the backend rejects it too).


function EtlPipelinePicker({ value, onChange, id, placeholder }: SmartPickerProps) {
  const q = useQuery({
    queryKey: ["etl", "for-node-picker"],
    queryFn: () => etlApi.list(),
    staleTime: 30_000,
  });

  const options = React.useMemo<ComboboxOption[]>(() => {
    return (q.data ?? [])
      .filter((p) => p.active_version_id != null)
      .map((p) => ({
        value: p.id,
        label: p.name,
        description: p.enabled ? p.description || "active" : "disabled",
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [q.data]);

  return (
    <Combobox
      id={id}
      options={options}
      value={(value as string) || null}
      onChange={(v) => onChange(v)}
      placeholder={placeholder ?? "Pick an ETL pipeline…"}
      loading={q.isLoading}
      emptyText="No ETL pipelines with an active version."
    />
  );
}


// ---------------------------------------------------------------------------
// node_id picker — Combobox of nodes in the current canvas
// ---------------------------------------------------------------------------


function NodeIdPicker({
  value,
  onChange,
  nodesInCanvas,
  currentNodeId,
  id,
  placeholder,
}: SmartPickerProps) {
  const options = React.useMemo<ComboboxOption[]>(() => {
    const list = nodesInCanvas ?? [];
    return list
      .filter((n) => n.id !== currentNodeId)
      .map((n) => ({
        value: n.id,
        label: n.label || n.id,
        description: n.kind,
      }));
  }, [nodesInCanvas, currentNodeId]);

  return (
    <Combobox
      id={id}
      options={options}
      value={(value as string) || null}
      onChange={(v) => onChange(v)}
      placeholder={placeholder ?? "Pick a node…"}
      emptyText="No other nodes on the canvas yet."
    />
  );
}


// ---------------------------------------------------------------------------
// canonical_model / canonical_version pickers
// ---------------------------------------------------------------------------


function useCanonicalCatalog() {
  return useQuery({
    queryKey: ["canonical", "all-for-picker"],
    queryFn: async () => {
      // Single page is enough — canonical types are bounded.
      const r = await canonicalApi.list({ page_size: 100 });
      return r.items;
    },
    staleTime: 60_000,
  });
}


function CanonicalModelPicker({
  value,
  onChange,
  config,
  id,
  placeholder,
}: SmartPickerProps) {
  const q = useCanonicalCatalog();

  // Distinct canonical types — ignore version for the model picker.
  const options = React.useMemo<ComboboxOption[]>(() => {
    const seen = new Set<string>();
    const out: ComboboxOption[] = [];
    for (const c of q.data ?? []) {
      if (seen.has(c.canonical_type)) continue;
      seen.add(c.canonical_type);
      out.push({
        value: c.canonical_type,
        label: c.canonical_type,
        description: c.description ?? undefined,
      });
    }
    return out.sort((a, b) => a.label.localeCompare(b.label));
  }, [q.data]);

  return (
    <Combobox
      id={id}
      options={options}
      value={(value as string) || null}
      onChange={(v) => {
        onChange(v);
        // Side-effect: if the chosen model has only one active version,
        // it'd be nice to auto-set canonical_version. But we don't have
        // sibling-write access here — the version picker will pick up
        // the change and offer the right options. Sibling read is fine.
        void config;
      }}
      placeholder={placeholder ?? "Pick a canonical model…"}
      loading={q.isLoading}
      emptyText="No canonical models defined yet."
      allowCustom
    />
  );
}


function CanonicalVersionPicker({
  value,
  onChange,
  config,
  id,
  placeholder,
}: SmartPickerProps) {
  const q = useCanonicalCatalog();
  const selectedModel = (config.canonical_model as string | undefined) || "";

  const options = React.useMemo<ComboboxOption[]>(() => {
    if (!selectedModel) return [];
    return (q.data ?? [])
      .filter((c: CanonicalSummary) => c.canonical_type === selectedModel)
      .sort((a, b) => b.version - a.version)
      .map((c) => ({
        value: String(c.version),
        label: `v${c.version}`,
        description: c.status, // active / draft / deprecated
      }));
  }, [q.data, selectedModel]);

  // Fallback to a plain number input when no model is picked yet — the
  // picker can't suggest versions without a model.
  if (!selectedModel) {
    return (
      <Input
        id={id}
        type="number"
        min={1}
        value={value !== undefined ? String(value) : ""}
        onChange={(e) =>
          onChange(e.target.value === "" ? undefined : Number(e.target.value))
        }
        placeholder={placeholder ?? "Pick a model first"}
      />
    );
  }

  return (
    <Combobox
      id={id}
      options={options}
      value={value !== undefined && value !== null ? String(value) : null}
      onChange={(v) => onChange(v ? Number(v) : undefined)}
      placeholder={placeholder ?? "Pick a version"}
      loading={q.isLoading}
      emptyText={`No versions defined for '${selectedModel}'.`}
    />
  );
}


// ---------------------------------------------------------------------------
// db_schema / db_table / db_columns pickers — n8n-style "resource
// locator" that introspects the chosen credential's database so the
// operator never types a schema / table / column name by hand.
// ---------------------------------------------------------------------------
//
// All three read the sibling ``credential_id`` (and schema / table) from
// the node config, so they go live the moment a credential is picked and
// re-fetch when the upstream field changes. Without a credential they
// fall back to a free-text input (so the field still works offline).


function _siblingCredential(config: Record<string, unknown>): string {
  return (config?.credential_id as string) || "";
}


function DbFreeText({ value, onChange, id, placeholder, hint }: SmartPickerProps & { hint: string }) {
  return (
    <div className="space-y-1">
      <Input
        id={id}
        value={(value as string) ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      <p className="text-[10px] text-muted-foreground">{hint}</p>
    </div>
  );
}


function DbSchemaPicker(props: SmartPickerProps) {
  const cred = _siblingCredential(props.config);
  const q = useQuery({
    queryKey: ["db-introspect", cred, "schemas"],
    queryFn: () => dbIntrospect({ credentialId: cred, level: "schemas" }),
    enabled: !!cred,
    staleTime: 30_000,
  });
  if (!cred) {
    return <DbFreeText {...props} hint="Pick a credential to list schemas." />;
  }
  const options: ComboboxOption[] = (q.data?.items ?? []).map((s) => ({
    value: s,
    label: s,
  }));
  return (
    <Combobox
      id={props.id}
      options={options}
      value={(props.value as string) || null}
      onChange={(v) => props.onChange(v)}
      placeholder={props.placeholder ?? "Pick a schema…"}
      loading={q.isLoading}
      emptyText={q.isError ? "Couldn't introspect (check the credential)." : "No schemas."}
      allowCustom
    />
  );
}


function DbTablePicker(props: SmartPickerProps) {
  const cred = _siblingCredential(props.config);
  const schema = (props.config?.schema_name as string) || "public";
  const q = useQuery({
    queryKey: ["db-introspect", cred, "tables", schema],
    queryFn: () => dbIntrospect({ credentialId: cred, level: "tables", schema }),
    enabled: !!cred,
    staleTime: 30_000,
  });
  if (!cred) {
    return <DbFreeText {...props} hint="Pick a credential to list tables." />;
  }
  const options: ComboboxOption[] = (q.data?.items ?? []).map((t) => ({
    value: t,
    label: t,
  }));
  return (
    <Combobox
      id={props.id}
      options={options}
      value={(props.value as string) || null}
      onChange={(v) => props.onChange(v)}
      placeholder={props.placeholder ?? "Pick a table…"}
      loading={q.isLoading}
      emptyText={q.isError ? "Couldn't introspect (check the credential)." : `No tables in ${schema}.`}
      allowCustom
    />
  );
}


// Multi-select over the table's columns. Stores a comma-joined string
// (the backend's matching_columns shape). Falls back to free text when
// there's no credential/table to introspect.
function DbColumnsPicker(props: SmartPickerProps) {
  const cred = _siblingCredential(props.config);
  const schema = (props.config?.schema_name as string) || "public";
  const table = (props.config?.table as string) || "";
  const q = useQuery({
    queryKey: ["db-introspect", cred, "columns", schema, table],
    queryFn: () =>
      dbIntrospect({ credentialId: cred, level: "columns", schema, table }),
    enabled: !!cred && !!table,
    staleTime: 30_000,
  });

  const selected = React.useMemo(
    () =>
      ((props.value as string) || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [props.value],
  );

  if (!cred || !table) {
    return (
      <DbFreeText
        {...props}
        hint="Pick a credential + table to choose columns, or type a comma-separated list."
      />
    );
  }

  const columns = q.data?.columns ?? [];
  function toggle(name: string) {
    const next = selected.includes(name)
      ? selected.filter((c) => c !== name)
      : [...selected, name];
    props.onChange(next.join(","));
  }

  return (
    <div className="space-y-1">
      <div className="flex max-h-40 flex-wrap gap-1 overflow-auto rounded-md border border-border bg-background p-1.5">
        {q.isLoading && (
          <span className="px-1 text-[11px] text-muted-foreground">Loading columns…</span>
        )}
        {q.isError && (
          <span className="px-1 text-[11px] text-danger">Couldn't introspect columns.</span>
        )}
        {!q.isLoading && !q.isError && columns.length === 0 && (
          <span className="px-1 text-[11px] text-muted-foreground">No columns.</span>
        )}
        {columns.map((c) => {
          const on = selected.includes(c.name);
          return (
            <button
              key={c.name}
              type="button"
              onClick={() => toggle(c.name)}
              title={c.type}
              className={cn(
                "rounded border px-1.5 py-0.5 text-[11px] font-mono transition",
                on
                  ? "border-accent bg-accent/15 text-accent"
                  : "border-border/60 text-muted-foreground hover:text-foreground",
              )}
            >
              {c.name}
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-muted-foreground">
        ON CONFLICT target — click columns that uniquely identify a row.
      </p>
    </div>
  );
}


// ---------------------------------------------------------------------------
// app_table picker — Combobox over /api/admin/apps/{appId}/tables
// ---------------------------------------------------------------------------
//
// ADR-0010 D10. App-kind workflows are bound to an app; the row_changed
// trigger + data_table node pick one of that app's data tables by id. The
// app id arrives via the ``appId`` prop (threaded WorkflowEditor → drawer →
// here). Without it we fall back to a free-text field so the form stays
// usable (e.g. when the workflow row hasn't loaded yet).


function AppTablePicker({ appId, value, onChange, id, placeholder }: SmartPickerProps) {
  const q = useQuery({
    queryKey: ["apps", appId, "tables", "for-picker"],
    queryFn: () => appsApi.listTables(appId!),
    enabled: !!appId,
    staleTime: 30_000,
  });

  const options = React.useMemo<ComboboxOption[]>(() => {
    return (q.data ?? [])
      .map((t) => ({
        value: t.id,
        label: t.name,
        description: `${t.columns_count} column${t.columns_count === 1 ? "" : "s"}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [q.data]);

  if (!appId) {
    return (
      <Input
        id={id}
        value={(value as string) ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? "table id"}
      />
    );
  }

  return (
    <Combobox
      id={id}
      options={options}
      value={(value as string) || null}
      onChange={(v) => onChange(v)}
      placeholder={placeholder ?? "Pick a data table…"}
      loading={q.isLoading}
      emptyText="This app has no data tables yet."
    />
  );
}


// ---------------------------------------------------------------------------
// app_unique_set picker — Combobox over the sibling table's unique sets
// ---------------------------------------------------------------------------
//
// The data_table node's upsert conflict target. Sibling-aware (like
// canonical_version): it reads ``config.table_id`` and lists THAT table's
// unique sets via GET /apps/{appId}/tables/{tableId}. Without appId or a
// chosen table it falls back to a free-text input so the form stays usable.


function AppUniqueSetPicker({ appId, config, value, onChange, id, placeholder }: SmartPickerProps) {
  const tableId = typeof config.table_id === "string" ? config.table_id : "";
  const q = useQuery({
    queryKey: ["apps", appId, "tables", tableId, "for-unique-set-picker"],
    queryFn: () => appsApi.getTable(appId!, tableId),
    enabled: !!appId && !!tableId,
    staleTime: 30_000,
  });

  const options = React.useMemo<ComboboxOption[]>(() => {
    return (q.data?.unique_sets ?? [])
      .map((s) => ({
        value: s.id,
        label: s.name,
        description: `${s.column_ids.length} column${s.column_ids.length === 1 ? "" : "s"}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [q.data]);

  if (!appId || !tableId) {
    return (
      <div className="space-y-1">
        <Input
          id={id}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder ?? "unique set id"}
        />
        <p className="text-[10px] text-muted-foreground">
          Pick a table first — the unique sets belong to it.
        </p>
      </div>
    );
  }

  return (
    <Combobox
      id={id}
      options={options}
      value={(value as string) || null}
      onChange={(v) => onChange(v)}
      placeholder={placeholder ?? "Pick a unique set…"}
      loading={q.isLoading}
      emptyText="This table has no unique sets yet."
    />
  );
}


// ---------------------------------------------------------------------------
// Unknown picker fallback — keeps the form usable when the backend
// adds a picker kind the frontend doesn't yet know about.
// ---------------------------------------------------------------------------


function UnknownPicker({
  pickerKind,
  value,
  onChange,
  id,
  placeholder,
}: SmartPickerProps) {
  return (
    <div className="space-y-1">
      <Input
        id={id}
        value={(value as string) ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      <p className="text-[10px] text-amber-600">
        Unknown picker kind <span className="font-mono">{pickerKind}</span>{" "}
        — rendering as text input. (Frontend may be behind the backend
        catalog; refresh to pull the latest.)
      </p>
    </div>
  );
}
